package fyi.moko.hotpl.data.service

import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.UsageEvents
import fyi.moko.hotpl.domain.service.UsageTracker
import fyi.moko.hotpl.domain.service.UsageTrackingPreference
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlin.random.Random

/** One usage event as stored remotely (`/users/{uid}/usage_events/{autoId}`). */
@Serializable
data class UsageEventDto(
    val name: String,
    val params: Map<String, String> = emptyMap(),
    /** Epoch millis on the device clock — good enough for ordering one person's day. */
    val at: Long,
    /** Random per process; groups a launch's events without any device identifier. */
    val sessionId: String,
    val build: String,
    val platform: String,
)

/** Where [BufferedUsageTracker] hands finished events; the Firestore one is the real sink. */
interface UsageEventSink {
    suspend fun record(uid: String, event: UsageEventDto)
}

/**
 * [UsageTracker] that stamps each event with session/build/platform, queues it, and writes
 * it out on [scope] once there is a signed-in uid. `track()` itself never suspends and never
 * throws; the preference is read at enqueue time so the "tracking off" event queued just
 * before the switch flips still goes out.
 *
 * Ordering is preserved (single consumer). A write that fails is dropped with a log line —
 * this is diagnostic data, not the journal, so no retry queue.
 */
class BufferedUsageTracker(
    private val auth: AuthService,
    private val preference: UsageTrackingPreference,
    private val sink: UsageEventSink,
    scope: CoroutineScope,
    private val build: String,
    private val platform: String,
    private val sessionId: String = newSessionId(),
    private val now: () -> Long,
) : UsageTracker {

    private val queue = Channel<UsageEventDto>(capacity = 256, onBufferOverflow = BufferOverflow.DROP_OLDEST)

    init {
        scope.launch {
            for (event in queue) {
                try {
                    val uid = auth.currentUserId.filterNotNull().first()
                    sink.record(uid, event)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    println("UsageTracker: dropped ${event.name}: ${e.message ?: e::class.simpleName}")
                }
            }
        }
        track(UsageEvents.SESSION_START, mapOf("build" to build, "platform" to platform))
    }

    override fun track(name: String, params: Map<String, String>) {
        if (!preference.enabled.value) return
        queue.trySend(
            UsageEventDto(
                name = name,
                params = params,
                at = now(),
                sessionId = sessionId,
                build = build,
                platform = platform,
            ),
        )
    }

    companion object {
        private const val SESSION_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789"

        fun newSessionId(): String = buildString {
            repeat(10) { append(SESSION_ALPHABET[Random.nextInt(SESSION_ALPHABET.length)]) }
        }
    }
}
